import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Button } from '@shadcn/ui/components/ui/button';
import { Badge } from '@shadcn/ui/components/ui/badge';
import { Loader2, PanelRight, AlarmClock, ArrowLeft, UserRound, Database } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@shadcn/ui/components/ui/dialog';
import { KeyboardSymbol, OSMetaKey } from '@shadcn/ui/components/keyboard-symbol';
import { ActionsCountBadge } from './actions-count-badge';
import { Skeleton } from '@shadcn/ui/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { ConnectionStatusBadge } from '../pages/actions/components/connection-status-badge';
import { PlaybookBadge } from './playbook-badge';
import { useSnoozeDialog } from './dialogs/snooze-dialog';
import { useSdk } from '../sdk';
import { PlaybookFilterBadge } from './playbook-filter-badge';
import { cn } from '@shadcn/ui/lib/utils';
import { useIsMobile } from '@shadcn/ui/hooks/use-mobile';
import {
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@shadcn/ui/components/ui/dropdown-menu';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { useAuth } from '../providers/authentication-provider';
import { SampleContact, SampleLibraryContent } from '../pages/admin/sample-library-page';
import { getZaplifySdk } from '@zaplify/sdk';
import { ChannelType } from '@zaplify/channel-accounts-shared';
import { MessageType } from '@zaplify/services-messaging-shared';
import { DeliveryStatus } from '@zaplify/campaigns';
import { useApps } from '../pages/account-management/apps/hooks/use-apps';

interface ActionsPageHeaderProps {
    onToggleDetails: () => void;
    isDetailsOpen: boolean;
    hideSectionPart?: boolean;
    forceShowBackButton?: boolean;
}

export const ConversationPageHeaderSkeleton: React.FC = () => {
    const location = useLocation();
    const isOnActionsPage = location.pathname.includes('actions');
    return (
        <header className="sticky top-0 w-full border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-14 items-center justify-between gap-4 px-4">
                {/* Left section */}
                <div className="flex items-center gap-4 w-[304px] border-r h-full pr-4">
                    <span className="font-medium text-2xl">{isOnActionsPage ? 'Actions' : 'Conversations'}</span>
                    <Skeleton className="w-8 h-8 rounded-full bg-background-brand-primary-subtle" />
                </div>
                {/* Center section */}
                <div className="flex flex-1 items-center justify-between pl-4">
                    <div className="flex items-center gap-3 grow">
                        <Skeleton className="w-48 h-2 bg-background-brand-primary-subtle" />
                    </div>

                    {/* Right section */}
                    <div className="flex items-center gap-4">
                        {isOnActionsPage && (
                            <Skeleton className="w-16 h-8 rounded-md bg-background-brand-primary-subtle" />
                        )}
                        <Skeleton className="w-16 h-8 rounded-md bg-background-brand-primary-subtle" />
                    </div>
                </div>
            </div>
        </header>
    );
};

export const ConversationPageHeader: React.FC<ActionsPageHeaderProps> = ({
    onToggleDetails,
    isDetailsOpen,
    hideSectionPart = false,
    forceShowBackButton = false,
}) => {
    const { prospectId } = useParams();
    const location = useLocation();
    const isOnActionsPage = location.pathname.includes('actions');
    const { openSnoozeDialog } = useSnoozeDialog();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const {
        accounts: {
            linkedin: { id: linkedinChannelAccountId },
        },
    } = useApps();
    const {
        prospect: { getProspectById },
    } = useSdk();

    // Load Sample modal state
    const [isLoadSampleModalOpen, setIsLoadSampleModalOpen] = useState(false);
    const [selectedSampleContact, setSelectedSampleContact] = useState<SampleContact>(null);

    // Authentication state
    const { authState } = useAuth();
    const { isGlobalAdmin, isTestSpace, userId, userOrganizationId } = authState;

    const shouldShowLoadSampleButton = isGlobalAdmin && isTestSpace;

    const { data } = useQuery(getProspectById(prospectId));

    const prospectData = useMemo(() => {
        if (data?.data) return data?.data;
    }, [data]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const activeElement = document.activeElement;
            const magicKey = event.ctrlKey || event.metaKey;
            const isTyping =
                activeElement?.tagName === 'INPUT' ||
                activeElement?.tagName === 'TEXTAREA' ||
                activeElement?.getAttribute('contenteditable') === 'true';

            if (magicKey && event.key === 'i' && !isTyping) {
                onToggleDetails();
            } else if (isOnActionsPage && prospectId && event.key === 's' && !isTyping) {
                openSnoozeDialog({ prospectId });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onToggleDetails, prospectId]);

    return (
        <header className="sticky top-0 w-full min-w-full h-14 border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-14 items-center justify-between gap-4 px-4">
                {/* Left section */}
                {!hideSectionPart && (
                    <div
                        className={cn(
                            'flex items-center gap-4 w-full min-w-full md:min-w-[304px] md:w-[304px] md:border-r justify-between h-full md:pr-4',
                            { 'hidden md:flex': prospectId }
                        )}
                    >
                        <div
                            className="chats-title flex items-center gap-4" // @IMPORTANT Needed for frigade tour (classname must be exact)
                        >
                            <span className={`font-medium text-2xl`}>
                                {isOnActionsPage ? 'Actions' : 'All conversations'}
                            </span>
                            {isOnActionsPage && (
                                <Suspense
                                    fallback={
                                        <Badge
                                            variant="secondary"
                                            className="w-fit bg-background-warning-secondary border-border-primary"
                                        >
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        </Badge>
                                    }
                                >
                                    <div className="flex items-center">
                                        <ActionsCountBadge />
                                    </div>
                                </Suspense>
                            )}
                        </div>
                        {isOnActionsPage && <PlaybookFilterBadge size="sm" />}
                        {/* <Button
                        variant="secondary"
                        size="icon"
                        className="ml-auto p-0 w-9 h-9 rounded-lg shadow-md shadow-background-primary-on-brand min-w-0 bg-background border border-b flex items-center bg-linear-to-r from-background-brand-primary/20 from-[6.25%] to-background-brand-primary/10 to-[84.37%] hover:bg-linear-to-r hover:from-background-brand-primary/20 hover:from-[2.25%] hover:to-background-brand-primary/10 hover:to-[40.37%] dark:from-background-brand-primary-subtle/30 dark:to-background-brand-primary/20 dark:hover:from-background-brand-primary-subtle/40 dark:hover:to-background-brand-primary/30"
                    >
                        <AndSendIcon variation="color-striped" />
                    </Button> */}
                    </div>
                )}

                {/* Right section */}
                {prospectData && (
                    <div className="flex flex-1 gap-4 items-center justify-between w-full">
                        <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                                'flex items-center gap-2',
                                forceShowBackButton ? 'flex' : 'sm:flex md:hidden'
                            )}
                            onClick={() => {
                                const { pathname, search } = window.location;
                                const newPath = pathname.split('/').slice(0, -1).join('/');
                                navigate(`${newPath}${search}`);
                            }}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            <span className="hidden xl:inline">Back</span>
                        </Button>
                        <div className="flex items-center gap-3">
                            {!isDetailsOpen && !isMobile ? (
                                <div className="flex flex-col gap-0">
                                    <div className="flex gap-2 items-center">
                                        <span className="font-medium text-lg line-clamp-1 text-nowrap">
                                            {prospectData.fullName}
                                        </span>
                                        <ConnectionStatusBadge key={prospectId} prospectId={prospectId} />
                                        <PlaybookBadge prospectId={prospectId} />
                                    </div>
                                    <span className="text-xs text-text-tertiary">
                                        {prospectData.organizationName && prospectData.occupationTitle
                                            ? `${prospectData.occupationTitle} - ${prospectData.organizationName}`
                                            : prospectData.linkedinHeadline
                                            ? prospectData.linkedinHeadline
                                            : ''}
                                    </span>
                                </div>
                            ) : (
                                <div className="flex flex-1 items-center gap-3">
                                    <span className="font-medium text-base md:text-lg line-clamp-1 text-nowrap">
                                        {prospectData.fullName}
                                    </span>
                                    <ConnectionStatusBadge key={prospectId} prospectId={prospectId} />
                                </div>
                            )}
                        </div>
                        {forceShowBackButton && !isMobile && <div className="flex-1" />}
                        <div className="flex items-center gap-2 justify-end">
                            {shouldShowLoadSampleButton && !isMobile && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2"
                                    onClick={() => setIsLoadSampleModalOpen(true)}
                                >
                                    <Database className="h-4 w-4" />
                                    <span className="hidden lg:inline">Load Sample</span>
                                </Button>
                            )}

                            {!isMobile && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex items-center gap-2"
                                                    onClick={() => openSnoozeDialog({ prospectId })}
                                                >
                                                    <AlarmClock className="h-4 w-4" />
                                                    <span className="hidden lg:inline">Snooze</span>
                                                </Button>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            Snooze action{' '}
                                            <KeyboardSymbol>
                                                <span>s</span>
                                            </KeyboardSymbol>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}

                            {!isDetailsOpen && !isMobile && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="flex items-center gap-2 animate-in fade-in-0 zoom-in-95"
                                                onClick={onToggleDetails}
                                            >
                                                <PanelRight className="h-4 w-4" />
                                                <span className="hidden xl:inline">View Details</span>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            Open contact details{' '}
                                            <KeyboardSymbol>
                                                <OSMetaKey />
                                                +i
                                            </KeyboardSymbol>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                            {isMobile && isOnActionsPage && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" className="flex items-center gap-2">
                                            <DotsHorizontalIcon className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        {shouldShowLoadSampleButton && (
                                            <DropdownMenuItem onClick={() => setIsLoadSampleModalOpen(true)}>
                                                <Database className="h-4 w-4" />
                                                <span>Load Sample</span>
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={onToggleDetails}>
                                            <UserRound className="h-4 w-4" />
                                            <span>View Details</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openSnoozeDialog({ prospectId })}>
                                            <AlarmClock className="h-4 w-4" />
                                            <span>Snooze Action</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Load Sample Modal */}
            <Dialog open={isLoadSampleModalOpen} onOpenChange={setIsLoadSampleModalOpen}>
                <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <DialogTitle>Sample Library</DialogTitle>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setIsLoadSampleModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => {
                                    console.log('Loading sample data...', {
                                        selectedSampleContact,
                                        currentContact: prospectData,
                                    });
                                    const messages = selectedSampleContact.conversation.messages.map(
                                        (message, index) => ({
                                            userId,
                                            organizationId: userOrganizationId,
                                            channelAccountId: linkedinChannelAccountId,
                                            prospectId: prospectId,
                                            // For fields like linkedinUserId, fullName, prospectFullName:
                                            // These might not be available in the email table and may need to be fetched separately
                                            // or set to appropriate defaults for email messages
                                            conversationId: `autogen_${prospectId}_${btoa(
                                                encodeURIComponent(message.subject || '(No subject)')
                                            )}`,
                                            channelType: ChannelType.LINKEDIN,
                                            actionType: MessageType.linkedinMessage,
                                            contentType: 'text/plain', // Default since we don't have email content in this query
                                            deliveryStatus: DeliveryStatus.DELIVERED,
                                            isSentFromApp: false,
                                            externalMessageId: `autogen_${prospectId}_${new Date().getTime()}_${index}`,
                                            direction: message.direction,
                                            subjectLine: message.subject || '(No subject)',
                                            content: message.text || '',
                                            sentOn: message.timestamp ? new Date(message.timestamp) : new Date(),
                                        })
                                    );

                                    let fullName = selectedSampleContact.contact.email.split('@')[0];
                                    let firstName = fullName;
                                    let lastName = '';
                                    if (fullName.includes('.')) {
                                        [firstName, lastName] = fullName.split('.');
                                        fullName = fullName.replace('.', ' ');
                                    }

                                    getZaplifySdk().profiles.user.replaceAndsendSampleContact({
                                        prospectId: prospectId,
                                        data: {
                                            prospect: {
                                                firstName,
                                                lastName,
                                                fullName,
                                            },
                                            messages: messages,
                                        },
                                    });
                                    setIsLoadSampleModalOpen(false);
                                }}
                            >
                                Load
                            </Button>
                        </div>
                    </DialogHeader>

                    <div className="mt-4">
                        <SampleLibraryContent onSelectedContactChange={setSelectedSampleContact} />
                    </div>
                </DialogContent>
            </Dialog>
        </header>
    );
};

export default ConversationPageHeader;
